import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { useModalDialog } from '../hooks/useModalDialog';
import { useSwipe } from '../hooks/useSwipe';
import { nearestSnap } from '../lib/swipe';

import s from './BottomSheet.module.css';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  labelledById: string;
  describedById?: string;
  children: ReactNode;
  /** Same semantics as Dialog: false blocks ESC, backdrop and the grip. */
  dismissible?: boolean;
  /**
   * Resting heights as fractions of the viewport, ascending. Without them the
   * sheet is as tall as its content, capped at 90dvh, and any drag past the
   * threshold closes it.
   */
  snapPoints?: readonly number[];
}

/** A grip drag this far closes a sheet with no snap points. */
const CLOSE_DRAG_PX = 80;

/**
 * A phone's dialog: rises from the bottom edge, respects the home indicator,
 * closes on a downward drag of the grip.
 *
 * Built on the same platform `<dialog>` as Dialog and Drawer — focus trap,
 * ESC and the top-layer backdrop all come from the browser. The grip is a real
 * button, so it is a close control for a screen reader and a keyboard as well
 * as a handle for a thumb. Dragging the sheet BODY is deliberately not
 * supported: with `touch-action: pan-y` the browser owns the pan and cancels
 * the pointer stream, so the grip is the one surface that is reliable.
 */
export function BottomSheet({
  open,
  onClose,
  labelledById,
  describedById,
  children,
  dismissible = true,
  snapPoints,
}: BottomSheetProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLButtonElement>(null);
  const [snap, setSnap] = useState<number | null>(() =>
    snapPoints !== undefined && snapPoints.length > 0 ? (snapPoints.at(-1) ?? null) : null,
  );
  useModalDialog(ref, open);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      if (dismissible && event.target === ref.current) onClose();
    },
    [dismissible, onClose],
  );

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      if (!dismissible) event.preventDefault();
    },
    [dismissible],
  );

  useSwipe(gripRef, {
    axis: 'y',
    direction: 'positive',
    threshold: snapPoints === undefined ? CLOSE_DRAG_PX : Number.POSITIVE_INFINITY,
    disabled: !dismissible,
    onMove: (delta) => {
      const panel = panelRef.current;
      if (panel) panel.style.transform = `translate3d(0, ${delta}px, 0)`;
    },
    onEnd: (_committed, delta) => {
      const panel = panelRef.current;
      if (panel) panel.style.transform = '';
      if (snapPoints === undefined || snapPoints.length === 0 || panel === null) return;
      // Where would the top edge rest if it stayed where the finger left it?
      const viewport = window.innerHeight || 1;
      const height = (panel.getBoundingClientRect().height - delta) / viewport;
      const floor = (snapPoints[0] ?? 0) / 2;
      const next = nearestSnap(height, snapPoints, floor);
      if (next === null) onClose();
      else setSnap(next);
    },
    onSwipe: onClose,
  });

  const style: CSSProperties | undefined =
    snap === null ? undefined : ({ '--sheet-height': `${snap * 100}dvh` } as CSSProperties);

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events,
                      jsx-a11y/no-noninteractive-element-interactions,
                      jsx-a11y/no-static-element-interactions --
       Backdrop click on the <dialog> closes the sheet; the native <dialog>
       already handles ESC via onClose, so no key handler is needed. The inner
       <div> only stops propagation so clicks inside the panel do not reach the
       backdrop handler — the same trade Dialog and Drawer make. */
    <dialog
      ref={ref}
      aria-labelledby={labelledById}
      aria-describedby={describedById}
      className={s.root}
      onClose={onClose}
      onCancel={handleCancel}
      onClick={handleClick}
    >
      <div ref={panelRef} className={s.panel} style={style} onClick={(e) => e.stopPropagation()}>
        <button
          ref={gripRef}
          type="button"
          className={s.grip}
          aria-label="Close sheet"
          disabled={!dismissible}
          onClick={onClose}
        >
          <span className={s.handle} aria-hidden="true" />
        </button>
        {children}
      </div>
    </dialog>
    /* eslint-enable jsx-a11y/click-events-have-key-events,
                     jsx-a11y/no-noninteractive-element-interactions,
                     jsx-a11y/no-static-element-interactions */
  );
}
